import { WEAPONS, WEAPON_ORDER, isFirearm, isWeaponId, type WeaponId } from './weaponData';

/**
 * What the player carries: owned weapons (fists always), the one in hand, rounds loaded in each gun
 * and spare rounds per gun. Pure data + rules, no game services, so the save migration and the tests
 * can use it directly. Serialised into the save as `SavedArsenal` (src/save/saveData.ts).
 */

export interface SavedArsenal {
  owned: string[];
  current: string;
  loaded: Record<string, number>;
  reserve: Record<string, number>;
}

export function emptyArsenal(): SavedArsenal {
  return { owned: ['fists'], current: 'fists', loaded: {}, reserve: {} };
}

/** What a gun-carrying player loses (busted: confiscated; wasted: dropped where they fell). */
export interface Haul {
  weapons: WeaponId[];
  loaded: Partial<Record<WeaponId, number>>;
  reserve: Partial<Record<WeaponId, number>>;
}

export class Arsenal {
  private readonly owned = new Set<WeaponId>(['fists']);
  private cur: WeaponId = 'fists';
  private readonly loadedRounds = new Map<WeaponId, number>();
  private readonly reserveRounds = new Map<WeaponId, number>();

  get current(): WeaponId {
    return this.cur;
  }

  /** Owned weapons in cycle order. */
  list(): WeaponId[] {
    return WEAPON_ORDER.filter((id) => this.owned.has(id));
  }

  has(id: WeaponId): boolean {
    return this.owned.has(id);
  }

  loaded(id: WeaponId): number {
    return this.loadedRounds.get(id) ?? 0;
  }

  reserve(id: WeaponId): number {
    return this.reserveRounds.get(id) ?? 0;
  }

  /** Add a weapon; a new gun comes loaded unless `loaded` says otherwise. Returns false if already owned. */
  give(id: WeaponId, loaded?: number): boolean {
    if (this.owned.has(id)) return false;
    this.owned.add(id);
    const f = WEAPONS[id].firearm;
    if (f) this.loadedRounds.set(id, clampInt(loaded ?? f.capacity, 0, f.capacity));
    return true;
  }

  /**
   * Take a weapon away with its rounds (a weapon lent for a mayhem challenge going back). Fists stay.
   * Returns false when it wasn't owned.
   */
  remove(id: WeaponId): boolean {
    if (id === 'fists' || !this.owned.delete(id)) return false;
    this.loadedRounds.delete(id);
    this.reserveRounds.delete(id);
    if (this.cur === id) this.cur = 'fists';
    return true;
  }

  /** Add spare rounds for a gun (capped). Returns how many were actually added. */
  addAmmo(id: WeaponId, n: number): number {
    const f = WEAPONS[id].firearm;
    if (!f || n <= 0) return 0;
    const before = this.reserve(id);
    const after = Math.min(f.maxReserve, before + Math.floor(n));
    this.reserveRounds.set(id, after);
    return after - before;
  }

  /** Room for more spare rounds? */
  ammoRoom(id: WeaponId): number {
    const f = WEAPONS[id].firearm;
    return f ? f.maxReserve - this.reserve(id) : 0;
  }

  select(id: WeaponId): boolean {
    if (!this.owned.has(id)) return false;
    this.cur = id;
    return true;
  }

  /** Next owned weapon in WEAPON_ORDER (wraps to fists). */
  cycle(): WeaponId {
    const list = this.list();
    const i = list.indexOf(this.cur);
    this.cur = list[(i + 1) % list.length] ?? 'fists';
    return this.cur;
  }

  /** Spend one round from the gun in hand. False when empty (or not a gun). */
  useRound(): boolean {
    const n = this.loaded(this.cur);
    if (!isFirearm(this.cur) || n <= 0) return false;
    this.loadedRounds.set(this.cur, n - 1);
    return true;
  }

  /** Can the gun in hand take more rounds from the reserve? */
  canReload(id: WeaponId = this.cur): boolean {
    const f = WEAPONS[id].firearm;
    return !!f && this.loaded(id) < f.capacity && this.reserve(id) > 0;
  }

  /** Move rounds from reserve into the gun. Returns rounds loaded. */
  reload(id: WeaponId = this.cur): number {
    const f = WEAPONS[id].firearm;
    if (!f) return 0;
    const need = f.capacity - this.loaded(id);
    const take = Math.min(need, this.reserve(id));
    if (take <= 0) return 0;
    this.loadedRounds.set(id, this.loaded(id) + take);
    this.reserveRounds.set(id, this.reserve(id) - take);
    return take;
  }

  /** Take every firearm and its ammunition off the player (melee weapons stay). */
  takeFirearms(): Haul {
    const haul: Haul = { weapons: [], loaded: {}, reserve: {} };
    for (const id of this.list()) {
      if (!isFirearm(id)) continue;
      haul.weapons.push(id);
      haul.loaded[id] = this.loaded(id);
      haul.reserve[id] = this.reserve(id);
      this.owned.delete(id);
      this.loadedRounds.delete(id);
    }
    // Spare rounds for guns you no longer have go too.
    for (const [id, n] of this.reserveRounds) {
      if (n > 0 && haul.reserve[id] === undefined) haul.reserve[id] = n;
    }
    this.reserveRounds.clear();
    if (!this.owned.has(this.cur)) this.cur = 'fists';
    return haul;
  }

  /** Give back a haul (picking up what you dropped). Returns true if anything was gained. */
  takeHaul(h: Haul): boolean {
    let gained = false;
    for (const id of h.weapons) {
      if (this.give(id, h.loaded[id] ?? 0)) gained = true;
    }
    for (const [id, n] of Object.entries(h.reserve) as [WeaponId, number][]) {
      if (this.addAmmo(id, n) > 0) gained = true;
    }
    return gained;
  }

  hasFirearm(): boolean {
    return this.list().some(isFirearm);
  }

  /**
   * Online: the server's arsenal replaces everything (weapons in your pockets, the one in hand, rounds
   * in each gun and spare). Counts are taken as given (the server already capped them).
   */
  syncFrom(owned: readonly WeaponId[], current: WeaponId, rounds: Readonly<Record<string, { mag: number; spare: number }>>): void {
    this.reset();
    for (const id of owned) if (id !== 'fists') this.owned.add(id);
    for (const [id, r] of Object.entries(rounds)) {
      if (!isWeaponId(id) || !isFirearm(id)) continue;
      this.loadedRounds.set(id, clampInt(r.mag, 0, WEAPONS[id].firearm!.capacity));
      if (r.spare > 0) this.reserveRounds.set(id, clampInt(r.spare, 0, 9999));
    }
    this.cur = this.owned.has(current) ? current : 'fists';
  }

  /** Back to fists only (new game). */
  reset(): void {
    this.owned.clear();
    this.owned.add('fists');
    this.cur = 'fists';
    this.loadedRounds.clear();
    this.reserveRounds.clear();
  }

  toSaved(): SavedArsenal {
    const loaded: Record<string, number> = {};
    const reserve: Record<string, number> = {};
    for (const [id, n] of this.loadedRounds) if (this.owned.has(id)) loaded[id] = n;
    for (const [id, n] of this.reserveRounds) if (n > 0) reserve[id] = n;
    return { owned: this.list(), current: this.cur, loaded, reserve };
  }

  /** Load from a (sanitised) save: unknown ids are dropped, counts clamped to the roster. */
  load(s: SavedArsenal): void {
    this.reset();
    for (const id of s.owned) if (isWeaponId(id) && id !== 'fists') this.give(id, s.loaded[id] ?? 0);
    for (const [id, n] of Object.entries(s.reserve)) if (isWeaponId(id)) this.addAmmo(id, n);
    if (isWeaponId(s.current) && this.owned.has(s.current)) this.cur = s.current;
  }
}

function clampInt(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, Math.floor(Number.isFinite(v) ? v : 0)));
}
